// LoginPage — Phase 6 Task 7 cross-room session 取得用ログイン画面.
//
// 着地点: `/login`. ホームの「ログイン」リンクから / 設定モーダルの
// 「admin_token が無い場合のログイン誘導」から / magic link expired/used
// 等のエラー画面の CTA から到達する.
//
// 確定方針 (consult 2026-05-04):
// - admin_token (localStorage) 経路は **ログイン操作不要** で room 単発で
//   既存実装が活用済 (RoomSettingsModal etc.). 本ページは cross-room
//   session (= cookie session) 取得のためだけに存在する.
// - AB. オーナーパスワード経路: roomId (UUID) + password をユーザがフォーム
//   入力. 200 で adminToken も返る → localStorage 保存 + pathname `/<id>` へ
//   navigate (= 即入室). cookie session も同時発行されている.
// - AC/AD. magic link 経路: email + purpose=recovery (cross-room) を送信.
//   成功 splash で「メールを確認してください」表示. 再送ボタンで何度でも
//   送信可 (rate limit 5/h は backend 任せ).
//
// `/login` 着地後のレイアウト:
//   1. オーナーパスワード経路フォーム (roomId + password)
//   2. or マジックリンク経路フォーム (email)
//   2 つを上下並列にして both visible にする (タブ切替よりシンプル、UX も
//   素直: 「片方失敗したらもう片方を試せる」).

import { useState } from 'react';

import {
  type EmailError,
  emailErrorText,
  type LoginPasswordError,
  type LoginUiOutcome,
  loginPasswordErrorText,
  mapLoginByOwnerPasswordOutcome,
  mapSendMagicLinkOutcome,
  OWNER_PASSWORD_MAX_LENGTH,
  OWNER_PASSWORD_MIN_LENGTH,
  type RoomIdError,
  roomIdErrorText,
  validateEmailForLogin,
  validateOwnerPasswordLogin,
} from '../application/loginForm.js';
import { createAdminEmailStore } from '../infrastructure/storage/AdminEmailStore.js';
import { AlphaDataWarningNotice } from './AlphaWarningBanner.js';
import { useSession } from './hooks/useSession.js';
import { navigateToHome, navigateToRoom } from './urlNavigation.js';

const goHome = (): void => {
  navigateToHome();
};

/* -----------------------------------------------------------------------------
 * UI feedback rendering helpers
 * --------------------------------------------------------------------------- */

const FeedbackLine = ({ ui }: { readonly ui: LoginUiOutcome | null }): React.ReactNode => {
  if (ui === null) return null;
  const tone =
    ui.kind === 'success'
      ? 'text-[var(--color-success)]'
      : ui.kind === 'info'
        ? 'text-[var(--color-accent-soft)]'
        : 'text-[var(--color-error)]';
  return (
    <p className={`mt-2 text-xs ${tone}`} role={ui.kind === 'error' ? 'alert' : 'status'}>
      {ui.message}
    </p>
  );
};

/* -----------------------------------------------------------------------------
 * 1. オーナーパスワード経路 (roomId + password)
 * --------------------------------------------------------------------------- */

const OwnerPasswordForm = (): React.ReactNode => {
  const { dispatcher, adminTokens } = useSession();
  const [roomId, setRoomId] = useState('');
  const [password, setPassword] = useState('');
  const [roomIdError, setRoomIdError] = useState<RoomIdError | null>(null);
  const [passwordError, setPasswordError] = useState<LoginPasswordError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [feedback, setFeedback] = useState<LoginUiOutcome | null>(null);

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (submitting) return;
    setFeedback(null);

    const v = validateOwnerPasswordLogin({ roomId, password });
    if (v.outcome !== 'ok') {
      setRoomIdError(v.errors.roomId ?? null);
      setPasswordError(v.errors.password ?? null);
      return;
    }
    setRoomIdError(null);
    setPasswordError(null);
    setSubmitting(true);
    const out = await dispatcher.loginByOwnerPassword(v.value);
    setSubmitting(false);
    setFeedback(mapLoginByOwnerPasswordOutcome(out));

    if (out.outcome === 'ok') {
      // adminToken を localStorage 保存 (cross-room API の bearer 経路に併用).
      adminTokens.save(out.roomId, out.adminToken);
      // 即 ルームに入る (= cookie session も同時発行されているので、後で
      // `/admin` に戻ってきても cross-room API が叩ける). pathname
      // `/<id>` に遷移、その他 view / action / token / name は全クリア.
      navigateToRoom(out.roomId);
    }
  };

  return (
    <form
      onSubmit={(e) => {
        void onSubmit(e);
      }}
      aria-label="オーナーパスワードでログイン"
      className="mb-6"
    >
      <h2 className="mb-2 text-sm font-bold text-[var(--color-text)]">
        オーナーパスワードでログイン
      </h2>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        ルーム作成時に設定したオーナーパスワードでログインできます。
      </p>

      <label className="mb-2 block text-sm">
        ルーム ID
        <input
          type="text"
          value={roomId}
          onChange={(e) => {
            setRoomId(e.target.value);
            if (roomIdError !== null) setRoomIdError(null);
            if (feedback !== null) setFeedback(null);
          }}
          disabled={submitting}
          autoComplete="off"
          spellCheck={false}
          autoCapitalize="off"
          autoCorrect="off"
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 font-mono text-sm text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder="00000000-0000-0000-0000-000000000000"
        />
        {roomIdError !== null && (
          <span className="mt-1 block text-xs text-[var(--color-error)]">
            {roomIdErrorText(roomIdError)}
          </span>
        )}
      </label>

      <label className="mb-3 block text-sm">
        オーナーパスワード
        <input
          type="password"
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError !== null) setPasswordError(null);
            if (feedback !== null) setFeedback(null);
          }}
          minLength={OWNER_PASSWORD_MIN_LENGTH}
          maxLength={OWNER_PASSWORD_MAX_LENGTH}
          disabled={submitting}
          autoComplete="current-password"
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder={`${OWNER_PASSWORD_MIN_LENGTH}〜${OWNER_PASSWORD_MAX_LENGTH} 文字`}
        />
        {passwordError !== null && (
          <span className="mt-1 block text-xs text-[var(--color-error)]">
            {loginPasswordErrorText(passwordError)}
          </span>
        )}
      </label>

      <button
        type="submit"
        disabled={submitting || roomId.length === 0 || password.length === 0}
        className="w-full rounded bg-[var(--color-accent)] px-4 py-2 text-base font-bold text-[var(--color-panel)] transition hover:brightness-110 disabled:opacity-60"
      >
        {submitting ? 'ログイン中…' : 'ログイン'}
      </button>

      <FeedbackLine ui={feedback} />
    </form>
  );
};

/* -----------------------------------------------------------------------------
 * 2. magic link 経路 (email + purpose=recovery)
 * --------------------------------------------------------------------------- */

const MagicLinkForm = (): React.ReactNode => {
  const { dispatcher } = useSession();
  const [email, setEmail] = useState('');
  const [emailErr, setEmailErr] = useState<EmailError | null>(null);
  const [submitting, setSubmitting] = useState(false);
  /** Splash 表示用. 'sent' になったら入力 form は disable し、再送ボタンを出す. */
  const [splash, setSplash] = useState<LoginUiOutcome | null>(null);

  const send = async (): Promise<void> => {
    if (submitting) return;

    const v = validateEmailForLogin(email);
    if (v.outcome !== 'ok') {
      setEmailErr(v.error);
      return;
    }
    setEmailErr(null);
    setSubmitting(true);
    const out = await dispatcher.sendMagicLink({ email: v.value, purpose: 'recovery' });
    setSubmitting(false);
    setSplash(mapSendMagicLinkOutcome(out));
    // Phase 6 Task 8: 入力された email を localStorage に保存しておくと、後で
    // magic link クリック先 (= AdminPage) のヘッダ「ログイン中: <email>」表示に
    // 使える. backend 側 enumeration 防止で `ok` は常に返るので 'success' のみ
    // 反応する (rate-limited / error は保存しない).
    if (out.outcome === 'ok') {
      createAdminEmailStore().save(v.value);
    }
  };

  const onSubmit = (e: React.FormEvent<HTMLFormElement>): void => {
    e.preventDefault();
    void send();
  };

  // splash が 'success' (= 200 で sent) なら、再送ボタン付きのスプラッシュ表示.
  // それ以外 (error / info) はフォームを残してエラー文言を出す.
  const sent = splash !== null && splash.kind === 'success';

  return (
    <form onSubmit={onSubmit} aria-label="マジックリンクでログイン" className="mb-2">
      <h2 className="mb-2 text-sm font-bold text-[var(--color-text)]">マジックリンクでログイン</h2>
      <p className="mb-3 text-xs text-[var(--color-text-muted)]">
        メール登録済のオーナーアカウントに、ログイン用のリンクをメールで送信します。
      </p>

      <label className="mb-3 block text-sm">
        メールアドレス
        <input
          type="email"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailErr !== null) setEmailErr(null);
            // 既送信状態 (splash) で入力を変えたらクリア = 別 email の再送導線.
            if (splash !== null) setSplash(null);
          }}
          disabled={submitting}
          autoComplete="email"
          spellCheck={false}
          className="mt-1 w-full rounded border border-white/30 bg-[var(--color-bg-soft)] px-2 py-1 text-base text-white outline-none focus:border-[var(--color-accent)] disabled:opacity-50"
          placeholder="owner@example.com"
        />
        {emailErr !== null && (
          <span className="mt-1 block text-xs text-[var(--color-error)]">
            {emailErrorText(emailErr)}
          </span>
        )}
      </label>

      <button
        type="submit"
        disabled={submitting || email.length === 0}
        className="w-full rounded border border-[var(--color-accent)]/60 bg-[var(--color-bg-soft)] px-4 py-2 text-base font-bold text-[var(--color-accent)] transition hover:bg-[var(--color-bg)] disabled:opacity-60"
      >
        {submitting ? '送信中…' : sent ? 'メールを再送' : 'マジックリンクを送信'}
      </button>

      <FeedbackLine ui={splash} />
    </form>
  );
};

/* -----------------------------------------------------------------------------
 * Page shell
 * --------------------------------------------------------------------------- */

export const LoginPage = (): React.ReactNode => {
  return (
    // px-4 + w-full max-w: 固定 w-[460px] はスマホ縦 (390/360px) で viewport を超える
    // (2026-07-03 実機 fb、ErrorScreen と同パターン)。縦は section の my-6 が担うので px のみ。
    <div className="grid h-full place-items-center overflow-y-auto px-4">
      <section
        className="my-6 w-full max-w-[460px] rounded border border-white/10 bg-[var(--color-panel)] p-6 shadow-xl"
        aria-label="ログイン"
      >
        <h1 className="mb-1 text-center text-lg font-bold tracking-wide text-[var(--color-accent)]">
          ログイン
        </h1>
        <p className="mb-5 text-center text-xs text-[var(--color-text-muted)]">
          別端末や別ブラウザからオーナー操作するためのログインです。
          <br />
          通常の入室はホーム画面から「ルームを作成」、または既存ルームの URL を直接開いてください。
        </p>

        {/* α 期データ削除警告 (2026-05-23 W2 公開向け). 2 form 共通の保存系
            注意なのでフォーム群の直前に 1 度だけ置く. */}
        <div className="mb-4">
          <AlphaDataWarningNotice />
        </div>

        <OwnerPasswordForm />

        <hr className="my-5 border-white/10" />

        <MagicLinkForm />

        <div className="mt-5 text-center">
          <button
            type="button"
            onClick={goHome}
            className="text-xs text-[var(--color-accent-soft)] underline transition hover:brightness-110"
          >
            ホームに戻る
          </button>
        </div>
      </section>
    </div>
  );
};
